import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, XCircle, RotateCcw, FileText, Calendar, User } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';

const SubmissionView = () => {
  const navigate = useNavigate();
  const { formId, submissionId } = useParams();
  const [form, setForm] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [formId, submissionId]);

  const fetchData = async () => {
    try {
      // Get form details
      const formRes = await api.get('/forms/' + formId);
      setForm(formRes.data);
      
      // Get submission details
      const subRes = await api.get('/forms/' + formId + '/submissions');
      const mySub = (subRes.data.submissions || []).find(function(s) {
        return s.submission_id === submissionId;
      });
      
      if (!mySub) {
        toast.error('Submission not found');
        navigate('/forms');
        return;
      }
      
      setSubmission(mySub);
    } catch (error) {
      toast.error('Failed to load submission');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</Badge>;
    if (status === 'rejected') return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Rejected</Badge>;
    if (status === 'sent_back') return <Badge variant="outline" className="border-amber-500 text-amber-600 flex items-center gap-1"><RotateCcw className="w-3 h-3" />Sent Back</Badge>;
    return <Badge variant="secondary" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending Approval</Badge>;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-';
    return value.toString();
  };

  const renderFieldValue = (field) => {
    const value = submission?.responses?.[field.field_id];
    
    if (field.field_type === 'rating') {
      const rating = parseInt(value) || 0;
      const maxRating = field.options?.maxRating || 5;
      return (
        <div className="flex gap-1">
          {Array.from({ length: maxRating }, function(_, i) {
            return (
              <svg key={i} className={'w-5 h-5 ' + (i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300')} viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            );
          })}
          <span className="ml-2 text-sm text-muted-foreground">({rating}/{maxRating})</span>
        </div>
      );
    }
    
    if (field.field_type === 'checkbox' || field.field_type === 'multiselect') {
      const values = Array.isArray(value) ? value : [];
      if (values.length === 0) return <span className="text-muted-foreground">-</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {values.map(function(v, i) {
            return <Badge key={i} variant="secondary">{v}</Badge>;
          })}
        </div>
      );
    }
    
    if (field.field_type === 'file') {
      return value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#4A9CC7] hover:underline">
          View uploaded file
        </a>
      ) : <span className="text-muted-foreground">No file uploaded</span>;
    }
    
    return <span>{formatValue(value)}</span>;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 flex items-center justify-center min-h-[50vh]">
          <p className="text-muted-foreground">Loading submission...</p>
        </div>
      </AppLayout>
    );
  }

  if (!form || !submission) return null;

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={function() { navigate('/forms'); }}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#4A9CC7]" />
              {form.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              {submission.is_receiver ? 'Feedback from ' + (submission.submitted_by_name || 'Anonymous') : 'Your Submission'}
            </p>
          </div>
          {getStatusBadge(submission.status)}
        </div>

        {/* Submission Meta */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Submitted</p>
                <p className="font-medium flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(submission.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Time</p>
                <p className="font-medium">
                  {new Date(submission.created_at).toLocaleTimeString()}
                </p>
              </div>
              {submission.approver_name && (
                <div>
                  <p className="text-muted-foreground">Approver</p>
                  <p className="font-medium flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {submission.approver_name}
                  </p>
                </div>
              )}
              {submission.approval_date && (
                <div>
                  <p className="text-muted-foreground">Reviewed On</p>
                  <p className="font-medium">
                    {new Date(submission.approval_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sent Back Warning */}
        {submission.status === 'sent_back' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="font-medium text-amber-800 mb-2">Changes Requested</h4>
            {submission.review_comments && submission.review_comments.length > 0 && (
              <p className="text-sm text-amber-700">
                {submission.review_comments[submission.review_comments.length - 1].comment || 'Please review and resubmit.'}
              </p>
            )}
            <Button 
              className="mt-3 bg-amber-600 hover:bg-amber-700"
              onClick={function() { navigate('/forms/' + formId + '/fill'); }}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Edit & Resubmit
            </Button>
          </div>
        )}

        {/* Rejection Notice */}
        {submission.status === 'rejected' && submission.review_comments && submission.review_comments.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">Submission Rejected</h4>
            <p className="text-sm text-red-700">
              {submission.review_comments[submission.review_comments.length - 1].comment || 'Your submission was not approved.'}
            </p>
          </div>
        )}

        {/* Approval Notice */}
        {submission.status === 'approved' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-medium text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Submission Approved
            </h4>
            {submission.review_comments && submission.review_comments.length > 0 && submission.review_comments[submission.review_comments.length - 1].comment && (
              <p className="text-sm text-green-700 mt-1">
                {submission.review_comments[submission.review_comments.length - 1].comment}
              </p>
            )}
          </div>
        )}

        {/* Submitted Responses */}
        <Card>
          <CardHeader>
            <CardTitle>{submission.is_receiver ? 'Feedback Content' : 'Your Responses'}</CardTitle>
            <CardDescription>
              {submission.is_receiver ? 'Feedback provided about you' : 'Read-only view of your submitted answers'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(form.fields || [])
              .filter(function(field) {
                // Hide user_selector field for receivers (they don't need to see who else was selected)
                if (field.field_type === 'user_selector' && submission.is_receiver) {
                  return false;
                }
                // Also hide if response is not present (backend filtered it out)
                if (field.field_type === 'user_selector' && !submission.responses[field.field_id]) {
                  return false;
                }
                return true;
              })
              .sort(function(a, b) { return a.order - b.order; })
              .map(function(field) {
                return (
                  <div key={field.field_id} className="border-b pb-4 last:border-0 last:pb-0">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <div className="text-base">
                      {renderFieldValue(field)}
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>

        {/* Review History */}
        {submission.review_comments && submission.review_comments.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Review History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {submission.review_comments.map(function(comment, idx) {
                  return (
                    <div key={idx} className="flex gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-[#4A9CC7]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{comment.reviewer_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.action === 'approve' ? 'Approved' : 
                             comment.action === 'reject' ? 'Rejected' : 
                             comment.action === 'resubmit' ? 'Resubmitted' : 'Sent Back'}
                          </Badge>
                        </div>
                        {comment.comment && (
                          <p className="text-sm text-muted-foreground mt-1">{comment.comment}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(comment.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={function() { navigate('/forms'); }}>
            Back to Forms
          </Button>
        </div>
      </div>
    </AppLayout>
  );
};

export default SubmissionView;
